import React, { useState, useEffect, useCallback } from 'react';
import './Usuarios.css';
import API_CONFIG from '../config';
import { apiFetch } from '../auth';

const BASE = API_CONFIG.BASE_URL + '/api/usuarios';
const EMPTY = { username: '', nombre: '', rol: 'USUARIO', password: '', activo: true };

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [guardando, setGuardando] = useState(false);
  const [error, setError]       = useState('');
  const [exito, setExito]       = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(BASE);
      setUsuarios(await res.json());
    } catch { setError('Error cargando usuarios.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(EMPTY);
    setError('');
    setMostrarForm(true);
  };

  const abrirEditar = (u) => {
    setEditando(u.id);
    setForm({ username: u.username, nombre: u.nombre, rol: u.rol, password: '', activo: u.activo });
    setError('');
    setMostrarForm(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true); setError('');
    try {
      const body = { ...form, activo: String(form.activo) };
      const url    = editando ? `${BASE}/${editando}` : BASE;
      const method = editando ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json()).message);
      setMostrarForm(false);
      setExito(editando ? 'Usuario actualizado.' : 'Usuario creado.');
      setTimeout(() => setExito(''), 3000);
      await cargar();
    } catch (e) { setError(e.message); }
    finally { setGuardando(false); }
  };

  const eliminar = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario "${u.username}"?`)) return;
    try {
      const res = await apiFetch(`${BASE}/${u.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Error al eliminar');
      setExito('Usuario eliminado.');
      setTimeout(() => setExito(''), 3000);
      await cargar();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="usuarios-page">
      <div className="usuarios-toolbar">
        <h2>👥 Gestión de Usuarios</h2>
        <button className="btn-primary" onClick={abrirNuevo}>+ Nuevo Usuario</button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {exito && <div className="alert alert-success">{exito}</div>}

      <div className="card">
        {loading ? <div className="loading">Cargando...</div> : (
          <table>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Nombre</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.length === 0 ? (
                <tr><td colSpan="5" style={{textAlign:'center',color:'#999',padding:'30px'}}>Sin usuarios</td></tr>
              ) : usuarios.map(u => (
                <tr key={u.id}>
                  <td><strong>{u.username}</strong></td>
                  <td>{u.nombre}</td>
                  <td>
                    <span className={`badge-rol badge-rol-${u.rol?.toLowerCase()}`}>{u.rol}</span>
                  </td>
                  <td>
                    <span className={`badge badge-${u.activo ? 'activo' : 'inactivo'}`}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td style={{display:'flex', gap:'6px'}}>
                    <button className="btn-info btn-sm" onClick={() => abrirEditar(u)}>✏️ Editar</button>
                    <button className="btn-danger btn-sm" onClick={() => eliminar(u)}>🗑️ Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {mostrarForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setMostrarForm(false)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h3>{editando ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
              <button className="modal-close" onClick={() => setMostrarForm(false)}>✕</button>
            </div>
            <form onSubmit={guardar} className="ficha-form">
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-group">
                <label>Usuario *</label>
                <input value={form.username} required disabled={!!editando}
                  onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                  placeholder="Ej: juan" />
              </div>
              <div className="form-group">
                <label>Nombre completo *</label>
                <input value={form.nombre} required
                  onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Juan Pérez" />
              </div>
              <div className="form-group">
                <label>Rol *</label>
                <select value={form.rol} onChange={e => setForm(p => ({ ...p, rol: e.target.value }))}>
                  <option value="ADMIN">Administrador</option>
                  <option value="USUARIO">Usuario</option>
                </select>
              </div>
              <div className="form-group">
                <label>{editando ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
                <input type="password" value={form.password} required={!editando}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Mínimo 4 caracteres" />
              </div>
              {editando && (
                <div className="form-group">
                  <label>Estado</label>
                  <select value={String(form.activo)} onChange={e => setForm(p => ({ ...p, activo: e.target.value === 'true' }))}>
                    <option value="true">Activo</option>
                    <option value="false">Inactivo</option>
                  </select>
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setMostrarForm(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : '✓ Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
